// TODO: split into multiple files.

import { useEffect } from 'react';
import {
  useMutation,
  useQuery,
  keepPreviousData,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { pb } from '../lib/pocketbase';
import { ClientResponseError } from 'pocketbase';
import type { ListResult } from 'pocketbase';
import {
  InventoryRecord,
  InventoryCategoriesRecord,
} from '../types/pocketbase-types';
import { useAuthProtected } from './auth.hooks';
import { notifications } from '@mantine/notifications';
import { IconX, IconInfoCircle, IconCheck } from '@tabler/icons-react';

// Always place null values at the bottom
function sortEmptyToBottom(
  data: { items: InventoryRecord[] },
  sortBy: string,
  direction: 'asc' | 'desc'
) {
  if (!data?.items) return data;

  const sorted = [...data.items].sort((a, b) => {
    const aEmpty = !a[sortBy] || a[sortBy] === 0;
    const bEmpty = !b[sortBy] || b[sortBy] === 0;

    if (aEmpty && !bEmpty) return 1;
    if (!aEmpty && bEmpty) return -1;
    if (aEmpty && bEmpty) return 0;

    const comparison = a[sortBy] < b[sortBy] ? -1 : 1;
    return direction === 'desc' ? -comparison : comparison;
  });

  return { ...data, items: sorted };
}

// Notification messages
const NOTIFICATIONS = {
  inventory: {
    create: {
      success: {
        title: 'פריט נוסף',
        getMessage: (name: string) => `הפריט ${name || ''} נוסף למלאי בהצלחה`,
      },
      other: {
        title: 'פריט חדש נוסף',
        getMessage: (name: string) =>
          `הפריט ${name || ''} נוסף על ידי משתמש אחר במערכת`,
      },
      error: {
        title: 'שגיאה',
        message: 'אירעה שגיאה בהוספת הפריט',
      },
    },
    update: {
      success: {
        title: 'פריט עודכן',
        getMessage: (name: string) => `הפריט ${name || ''} עודכן בהצלחה`,
      },
      other: {
        title: 'פריט עודכן',
        getMessage: (name: string) =>
          `הפריט ${name || ''} עודכן על ידי משתמש אחר במערכת`,
      },
      error: {
        title: 'שגיאה',
        message: 'אירעה שגיאה בעדכון הפריט',
      },
    },
    delete: {
      success: {
        title: 'פריט נמחק',
        getMessage: (name: string) => `הפריט ${name || ''} נמחק מהמערכת`,
      },
      other: {
        title: 'פריט נמחק',
        getMessage: (name: string) =>
          `הפריט ${name || ''} נמחק על ידי משתמש אחר במערכת`,
      },
      error: {
        title: 'שגיאה',
        message: 'אירעה שגיאה במחיקת הפריט',
      },
    },
  },
};

function showNotification(
  type: 'success' | 'error' | 'other',
  notification: {
    title: string;
    message?: string;
    getMessage?: (name: string) => string;
  },

  itemName?: string
) {
  const message =
    notification.getMessage && itemName
      ? notification.getMessage(itemName)
      : notification.message;

  const config = {
    title: notification.title,
    message,
    color: type === 'error' ? 'red' : 'green',
    icon:
      type === 'error' ? (
        <IconX size={16} />
      ) : type === 'success' ? (
        <IconCheck size={16} />
      ) : (
        <IconInfoCircle size={16} />
      ),
  };

  // Add additional properties for subscription notifications
  if (type === 'other') {
    Object.assign(config, {
      withCloseButton: true,
      autoClose: false,
    });
  }

  notifications.show(config);
}

export function useGetInventory(
  collectionName: string = 'inventory',
  page: number,
  pageSize: number,
  sortBy: keyof InventoryRecord = 'created',
  direction: 'asc' | 'desc' = 'desc',
  filters: {
    query?: string;
    nameQuery?: string;
    categories?: string[];
    manufacturers?: string[];
    currencies?: {
      sellingPrice?: string[];
      purchasePrice?: string[];
      estimatedSellingPrice?: string[];
    };
    globalCurrency?: string;
    priceRange?: [number | null, number | null];
  } = {}
): UseQueryResult<
  ListResult<
    InventoryRecord & { expand: { categories: InventoryCategoriesRecord[] } }
  >
> {
  const queryFn = async () => {
    const filterConditions = [];

    if (filters.query) {
      // First try exact matches
      const exactMatch = `(product_name = "${filters.query}" || catalog_number = "${filters.query}" || RFID = "${filters.query}")`;

      // Then try partial matches on important fields
      const partialMatch = `(product_name ~ "${filters.query}" || catalog_number ~ "${filters.query}" || manufacturer ~ "${filters.query}")`;

      // Finally try less important fields
      const secondaryMatch = `(model ~ "${filters.query}" || comments ~ "${filters.query}" || original_catalog_number ~ "${filters.query}")`;

      filterConditions.push(
        `(${exactMatch} || ${partialMatch} || ${secondaryMatch})`
      );
    }

    if (filters.nameQuery) {
      if (filters.query) {
        // If we have both queries, use AND to combine them
        filterConditions.push(`(product_name ~ "${filters.nameQuery}")`);
      } else {
        // If we only have nameQuery, just search by name
        filterConditions.push(`(product_name ~ "${filters.nameQuery}")`);
      }
    }

    if (filters.categories?.length) {
      const categoryConditions = filters.categories
        .map((category) => `categories.name ?~ "${category}"`)
        .join(' || ');
      filterConditions.push(`(${categoryConditions})`);
    }

    if (filters.manufacturers?.length) {
      const manufacturerConditions = filters.manufacturers
        .map((manufacturer) => `manufacturer = "${manufacturer}"`)
        .join(' || ');
      filterConditions.push(`(${manufacturerConditions})`);
    }

    if (filters.globalCurrency) {
      filterConditions.push(
        `(selling_price_currency = "${filters.globalCurrency}" || purchase_price_currency = "${filters.globalCurrency}" || estimated_selling_price_currency = "${filters.globalCurrency}")`
      );
    } else if (filters.currencies) {
      if (filters.currencies.sellingPrice?.length) {
        const sellingPriceConditions = filters.currencies.sellingPrice
          .map((currency) => `selling_price_currency = "${currency}"`)
          .join(' || ');
        filterConditions.push(`(${sellingPriceConditions})`);
      }

      if (filters.currencies.purchasePrice?.length) {
        const purchasePriceConditions = filters.currencies.purchasePrice
          .map((currency) => `purchase_price_currency = "${currency}"`)
          .join(' || ');
        filterConditions.push(`(${purchasePriceConditions})`);
      }

      if (filters.currencies.estimatedSellingPrice?.length) {
        const estimatedSellingPriceConditions =
          filters.currencies.estimatedSellingPrice
            .map(
              (currency) => `estimated_selling_price_currency = "${currency}"`
            )
            .join(' || ');
        filterConditions.push(`(${estimatedSellingPriceConditions})`);
      }
    }

    if (filters.priceRange) {
      const [min, max] = filters.priceRange;
      const priceField =
        sortBy === 'selling_price'
          ? 'selling_price'
          : sortBy === 'purchase_price'
            ? 'purchase_price'
            : 'estimated_selling_price';

      if (min !== null) {
        filterConditions.push(`${priceField} >= ${min}`);
      }
      if (max !== null) {
        filterConditions.push(`${priceField} <= ${max}`);
      }
    }

    return await pb.collection(collectionName).getList(page, pageSize, {
      sort: `${direction === 'desc' ? '-' : ''}${sortBy}`,
      filter: filterConditions.join(' && '),
      expand: 'categories',
    });
  };

  const protectedQueryFn = useAuthProtected(queryFn);

  return useQuery({
    queryKey: [collectionName, page, pageSize, sortBy, direction, filters],
    queryFn: protectedQueryFn,
    // Make sure we still have data visible while the fetching (prevent UI flash).
    placeholderData: keepPreviousData,
    // TODO: check if this is ok
    // Process custom sorting only for certain price and date fields
    select:
      sortBy === 'purchase_price' ||
      sortBy === 'date_acquired' ||
      sortBy === 'date_sold'
        ? (data) => sortEmptyToBottom(data, sortBy, direction)
        : undefined,
  });
}

export function useGetCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const records = await pb
        .collection('inventory_categories')
        .getFullList<InventoryCategoriesRecord>();
      return records.map((record) => record.name);
    },
  });
}

export function useGetCategoryRecords() {
  return useQuery({
    queryKey: ['category_records'],
    queryFn: async () => {
      const records = await pb
        .collection('inventory_categories')
        .getFullList<InventoryCategoriesRecord>();
      return records;
    },
  });
}

export function useGetManufacturers() {
  const queryFn = async () => {
    const records = await pb.collection('inventory').getFullList({
      fields: 'manufacturer',
    });
    return [
      ...new Set(
        records.flatMap((record) => record.manufacturer).filter(Boolean)
      ),
    ];
  };

  const protectedQueryFn = useAuthProtected(queryFn);
  return useQuery({
    queryKey: ['manufacturers'],
    queryFn: protectedQueryFn,
  });
}

export function useGetMinMaxPrices() {
  const queryFn = async () => {
    const lowestPrice = await pb
      .collection('inventory')
      .getFirstListItem('purchase_price != null', {
        sort: 'purchase_price',
      });
    const highestPrice = await pb
      .collection('inventory')
      .getFirstListItem('purchase_price != null', {
        sort: '-purchase_price',
      });

    return {
      min: lowestPrice?.purchase_price || 0,
      max: highestPrice?.purchase_price || 0,
    };
  };

  const protectedQueryFn = useAuthProtected(queryFn);

  return useQuery({
    queryKey: ['price-range'],
    queryFn: protectedQueryFn,
  });
}

export function usePostNewItem() {
  const mutationFn = async (data: InventoryRecord) => {
    return await pb.collection('inventory').create(data);
  };

  const protectedMutationFn = useAuthProtected(mutationFn);

  return useMutation({
    mutationFn: protectedMutationFn,
  });
}

export function useDeleteItem() {
  const queryClient = useQueryClient();

  const mutationFn = async (id: string) => {
    try {
      const item = await pb.collection('inventory').getOne(id);
      const response = await pb.collection('inventory').delete(id);
      showNotification(
        'success',
        NOTIFICATIONS.inventory.delete.success,
        item.product_name
      );
      return response;
    } catch (error) {
      showNotification('error', NOTIFICATIONS.inventory.delete.error);
      throw error;
    }
  };

  const protectedMutationFn = useAuthProtected(mutationFn);

  return useMutation({
    mutationFn: protectedMutationFn,
    onSuccess: async (_, deletedId) => {
      const deletedItem = queryClient
        .getQueryData<any>(['inventory'])
        ?.items?.find((item) => item.id === deletedId);

      // Invalidate inventory queries
      queryClient.invalidateQueries({ queryKey: ['inventory'] });

      // If the item had categories, invalidate categories
      if (deletedItem?.categories?.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['categories'] });
      }

      // Only invalidate price range if this was a min/max price item
      const priceRange = queryClient.getQueryData<{ min: number; max: number }>(
        ['price-range']
      );
      if (priceRange && deletedItem?.purchase_price) {
        const { min, max } = priceRange;
        if (
          deletedItem.purchase_price === min ||
          deletedItem.purchase_price === max
        ) {
          queryClient.invalidateQueries({ queryKey: ['price-range'] });
        }
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });
}

export function useUpdateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      formData,
    }: {
      id: string;
      formData: FormData;
    }) => {
      try {
        const item = await pb.collection('inventory').getOne(id);
        const response = await pb.collection('inventory').update(id, formData);
        showNotification(
          'success',
          NOTIFICATIONS.inventory.update.success,
          item.product_name
        );
        return response;
      } catch (error) {
        showNotification('error', NOTIFICATIONS.inventory.update.error);
        console.error('PocketBase update error:', error);
        if (error instanceof ClientResponseError) {
          console.error('Response data:', error.data);
          console.error('Status code:', error.status);
          throw new Error(
            error.data?.message || 'Failed to update item in database'
          );
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useCreateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      try {
        const record = await pb.collection('inventory').create(formData);
        showNotification(
          'success',
          NOTIFICATIONS.inventory.create.success,
          record.product_name
        );
        return record;
      } catch (error) {
        showNotification('error', NOTIFICATIONS.inventory.create.error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useSubscription(collectionName: string = 'inventory') {
  const queryClient = useQueryClient();

  useEffect(() => {
    console.log(`Setting up subscription for ${collectionName}`);

    pb.collection(collectionName).subscribe('*', async (e) => {
      try {
        console.log('Received real-time event:', e.action, e.record, e);

        // Show notification based on action type
        if (e.action === 'create') {
          showNotification(
            'other',
            NOTIFICATIONS.inventory.create.other,
            e.record.product_name
          );
        } else if (e.action === 'delete') {
          showNotification(
            'other',
            NOTIFICATIONS.inventory.delete.other,
            e.record.product_name
          );
        } else if (e.action === 'update') {
          showNotification(
            'other',
            NOTIFICATIONS.inventory.update.other,
            e.record.product_name
          );
        }

        // Invalidate all queries for this collection regardless of pagination/filters
        await queryClient.invalidateQueries({
          queryKey: [collectionName],
          exact: false,
          refetchType: 'all',
        });

        // Also invalidate categories if they might have changed
        if (e.record.categories?.length > 0) {
          await queryClient.invalidateQueries({
            queryKey: ['categories'],
            exact: false,
          });
        }
      } catch (error) {
        console.error('Subscription error:', error);
      }
    });

    return () => {
      pb.collection(collectionName).unsubscribe();
    };
  }, [collectionName, queryClient]);
}
